import { DOCUMENT, CommonModule, NgOptimizedImage } from '@angular/common';
import { inject, NgModule } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ResolveFn, RouterModule, Routes } from '@angular/router';
import { of } from 'rxjs';
import { applyPageSeo, resolvePageUrl } from '../../shared/seo/page-seo';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {LottieCardComponent} from '../../shared/components/lottie-card/lottie-card.component';
import {UserDetailComponent} from '../../shared/components/user-detail/user-detail.component';
import { AuthGuard } from '../auth/services/auth.guard';
import { PackCardComponent } from "../../shared/components/pack-card/pack-card.component";
import { PackCardLoaderComponent } from "../../shared/components/pack-card-loader/pack-card-loader.component";
import { SharedModule } from '../../shared/shared-module';
import { AvatarComponent } from '../../shared/components/avatar/avatar';
import { DashboardComponent } from './components/layout/dashboard/dashboard.component';
import { HeaderComponent } from './components/layout/header/header.component';
import { FooterComponent } from './components/layout/footer/footer.component';
import { LandingPageComponent } from './components/layout/landing-page/landing-page.component';
import { BrowseAssetsByKeywordsComponent } from './components/layout/landing-page/browse-assets-by-keywords/browse-assets-by-keywords.component';
import { browseKeywordCategoryMatcher } from './components/layout/landing-page/browse-assets-by-keywords/browse-keyword-route';
import { ContributorComponent } from './components/contributor/contributor.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

const CONTRIBUTORS_LIST_PATH = '/contributors-list';

const contributorsListSeoResolver: ResolveFn<true> = () => {
  applyPageSeo(
    {
      title: inject(Title),
      meta: inject(Meta),
      document: inject(DOCUMENT),
    },
    {
      pagePath: CONTRIBUTORS_LIST_PATH,
      pageTitle: 'Designers | CreativeStall.com',
      pageDescription:
        'Browse designers and explore creative assets shared on Creative Stall.',
      pageUrl: resolvePageUrl(CONTRIBUTORS_LIST_PATH, undefined, inject(DOCUMENT)),
    }
  );
  return of(true);
};

const routes: Routes = [
  {
    path: '',
    component: DashboardComponent,
    children: [

      // HOME
      {
        path: '',
        component: LandingPageComponent,
        pathMatch: 'full',
      },

      {
        path: 'faq',
        loadChildren: () =>
          import('./components/pages/faq/faq.module').then((m) => m.FaqModule),
        data: {
          showGallery: false
        }
      },
      {
        path: '',
        loadChildren: () =>
          import('./components/pages/legal/legal.module')
            .then(m => m.LegalModule),
      },

      {
        path: 'profile',
        canActivate:[AuthGuard],
        loadChildren: () =>
          import('../contributor/components/profile/profile.module').then((m) => m.ProfileModule),
        data: {
          showGallery: false
        }
      },
      {
        path: 'gallery',
        loadChildren: () =>
          import('./components/pages/gallery/gallery.module').then((m) => m.GalleryModule),
        data: {
          showGallery: false
        }
      },
      {
        path: 'festivities/:parent',
        loadChildren: () =>
          import('./components/pages/gallery/festivies-gallery/festivities-gallery.module').then((m) => m.FestivitiesGalleryModule),
        data: {
          showGallery: false
        }
      },
      {
        path: 'designers',
        loadChildren: () =>
          import('./components/pages/contributors/contributors.module').then((m) => m.ContributorsModule),
        data: {
          showGallery: false
        }
      },
      {
        path: 'contributors-list',
        component: ContributorComponent,
        resolve: { seo: contributorsListSeoResolver },
        data: {
          showGallery: false
        }
      },
      {
        matcher: browseKeywordCategoryMatcher,
        loadComponent: () =>
          import('./components/layout/landing-page/browse-assets-by-keywords/browse-keywords-detail.component').then(
            (m) => m.BrowseKeywordsDetailComponent
          ),
        data: {
          showGallery: false,
        },
      },
      {
        path:'pricing',
        loadChildren: ()=> import('../contributor/components/profile/subscription/subscription.module').then((m)=> m.SubscriptionModule),
        data: {
          showGallery: false
        }
      },
      {
        path: 'tool',
        loadChildren: ()=> import('./components/pages/tools/tools.module').then((m)=> m.ToolsModule),
        data: {
          showGallery: false
        }
      },
      { path: 'contact-us', loadChildren: () => import('./components/pages/contact/contact.module').then(m => m.ContactModule) },
    ]
  },
  { path: 'faq', loadChildren: () => import('./components/pages/faq/faq.module').then(m => m.FaqModule) },

];

@NgModule({
  declarations: [DashboardComponent, HeaderComponent, FooterComponent, ContributorComponent, LandingPageComponent],
  exports: [HeaderComponent, FooterComponent],
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    ReactiveFormsModule,
    RouterModule.forChild(routes),
    NgOptimizedImage,
    UserDetailComponent,
    LottieCardComponent,
    PackCardComponent,
    PackCardLoaderComponent,
    AvatarComponent,
    NgxSkeletonLoaderModule,
    BrowseAssetsByKeywordsComponent,
],
})
export class PublicModule {
}
